<template>
  <div v-if="isPwaInstallable" class="pwa-install-button" @click="installPwa">
    <font-awesome-icon icon="fa-solid fa-download" />
    <span class="install-text">Install App</span>
  </div>
  <div v-else-if="shouldShowOpenAppButton" class="pwa-open-button" @click="openPwa">
    <font-awesome-icon icon="fa-solid fa-external-link-alt" />
    <span class="install-text">Open App</span>
  </div>
</template>

<script>
export default {
  name: "PwaInstallButton",
  data() {
    return {
      deferredPrompt: null, // Store the beforeinstallprompt event
      isAppInstalled: false, // Track if the app is installed
      isStandalone: false, // Track if we're in standalone mode
      checkInterval: null // Interval for periodically checking if app is installed
    };
  },
  computed: {
    isPwaInstallable() {
      return !!this.deferredPrompt;
    },
    // Show "Open App" button if we're in browser mode and the app is installed
    shouldShowOpenAppButton() {
      return this.isAppInstalled && !this.isStandalone;
    }
  },
  created() {
    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', this.handleAppInstalled);

    // Check if we're in standalone mode (PWA)
    this.checkStandaloneMode();

    // Check if the app has been installed before
    this.checkIfAppInstalled();

    // Start periodic checking for installed PWA
    this.startPeriodicCheck();

    // Listen for display mode changes
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      this.isStandalone = e.matches;
    });

    // Listen for visibility changes to check when user returns to the page
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    // Remove event listeners to prevent memory leaks
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Remove display mode change listener
    window.matchMedia('(display-mode: standalone)').removeEventListener('change', () => {});

    // Clear the periodic check interval
    this.stopPeriodicCheck();
  },
  methods: {
    // Check if we're in standalone mode (PWA)
    checkStandaloneMode() {
      // Check for various indicators across different platforms
      this.isStandalone = 
        // Standard way to detect standalone mode
        window.matchMedia('(display-mode: standalone)').matches || 
        // iOS specific
        window.navigator.standalone === true || 
        // Android TWA
        document.referrer.includes('android-app://') ||
        // Additional check for iOS/iPadOS
        (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream && 'standalone' in window.navigator && window.navigator.standalone);
    },

    // Start periodic checking for installed PWA
    startPeriodicCheck() {
      // Check every 30 seconds if the app is installed
      this.checkInterval = setInterval(() => {
        this.checkIfAppInstalled();

        // Also try to detect if the app has been installed through other means
        this.checkRelatedApps();
      }, 30000); // 30 seconds
    },

    // Stop the periodic check
    stopPeriodicCheck() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    },

    // Handle visibility change (when user returns to the page)
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // When the user returns to the page, check if the app is installed
        this.checkIfAppInstalled();
        this.checkRelatedApps();
      }
    },

    // Try to use the getInstalledRelatedApps API if available
    async checkRelatedApps() {
      if ('getInstalledRelatedApps' in navigator) {
        try {
          const relatedApps = await navigator.getInstalledRelatedApps();
          if (relatedApps && relatedApps.length > 0) {
            // If we find a related app, mark the app as installed
            localStorage.setItem('pwaInstalled', 'true');
            this.isAppInstalled = true;
          }
        } catch (error) {
          console.error('Error checking related apps:', error);
        }
      }
    },

    // Check if the app has been installed by looking for a flag in localStorage
    checkIfAppInstalled() {
      this.isAppInstalled = localStorage.getItem('pwaInstalled') === 'true';
    },

    async installPwa() {
      if (!this.deferredPrompt) {
        console.log('PWA installation prompt not available');
        return;
      }

      // Show the installation prompt
      this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const choiceResult = await this.deferredPrompt.userChoice;

      // Reset the deferredPrompt variable
      this.deferredPrompt = null;

      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the PWA installation');
        // Mark the app as installed in localStorage
        localStorage.setItem('pwaInstalled', 'true');
        this.isAppInstalled = true;
      } else {
        console.log('User dismissed the PWA installation');
      }
    },

    // Open the installed PWA
    openPwa() {
      // Try different approaches to open the PWA

      // 1. Try using the Web App Manifest's start_url if available
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        try {
          // Fetch the manifest to get the start_url via axios
          this.axios.get(manifestLink.href)
            .then(response => response.data)
            .then(manifest => {
              if (manifest && manifest.start_url) {
                const startUrl = new URL(manifest.start_url, window.location.origin).toString();
                // Navigate directly instead of opening a new tab
                window.location.href = startUrl;
                console.log('Opening PWA using manifest start_url:', startUrl);
                return;
              }
            })
            .catch(err => {
              console.error('Error fetching manifest:', err);
              this.fallbackOpenPwa();
            });
        } catch (e) {
          console.error('Error processing manifest:', e);
          this.fallbackOpenPwa();
        }
      } else {
        this.fallbackOpenPwa();
      }
    },

    // Fallback method to open PWA
    fallbackOpenPwa() {
      // Use the current URL with a special parameter
      const currentUrl = window.location.href;
      const pwaUrl = new URL(currentUrl);

      // Add a timestamp to force a new URL that might trigger the PWA
      pwaUrl.searchParams.set('pwa', 'true');
      pwaUrl.searchParams.set('t', Date.now());

      // Navigate directly instead of opening a new tab
      window.location.href = pwaUrl.toString();

      console.log('Attempting to open PWA with URL:', pwaUrl.toString());
    },

    handleBeforeInstallPrompt(e) {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      this.deferredPrompt = e;
      console.log('PWA: Installation prompt is available');
    },

    handleAppInstalled(e) {
      // Clear the deferredPrompt so the button won't show
      this.deferredPrompt = null;
      // Mark the app as installed in localStorage
      localStorage.setItem('pwaInstalled', 'true');
      this.isAppInstalled = true;
      console.log('PWA: Application was installed');
    }
  }
};
</script>

<style scoped>
.pwa-install-button, .pwa-open-button {
  position: fixed;
  bottom: 20px;
  right: 20px; /* Position to the far right of the page */
  color: white;
  border-radius: 8px; /* Rectangular with rounded corners */
  width: 100px; /* Wider for rectangular shape */
  height: 40px; /* Shorter height */
  display: flex;
  flex-direction: row; /* Change to row for horizontal layout */
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
  padding: 0 10px; /* Add horizontal padding */
}

.pwa-install-button {
  background-color: #558B2F;
}

.pwa-open-button {
  background-color: #28a745; /* Green color for the open button */
}

.pwa-install-button:hover, .pwa-open-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.4);
}

.pwa-install-button:hover {
  background-color: #0056b3;
}

.pwa-open-button:hover {
  background-color: #218838;
}

.install-text {
  font-size: 12px;
  margin-left: 8px; /* Add margin to the left for spacing from the icon */
}

/* Media query for mobile devices */
@media (max-width: 768px) {
  .pwa-install-button, .pwa-open-button {
    width: 110px; /* Slightly wider on mobile */
    height: 40px;
    left: 15px; /* Adjust position on mobile */
  }

  .install-text {
    font-size: 12px;
  }
}
</style>
